import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Smartphone,
  Users,
  ShoppingCart,
  Rocket,
  Layers,
  Code2,
  Cpu,
  Sparkles,
  ChevronDown,
  ClipboardList,
  PlayCircle,
  Database,
  Globe,
  Lock,
  FolderGit2,
  Wand2,
  Terminal,
  Cloud,
  PhoneCall,
} from "lucide-react";
import { Link } from "react-router-dom";

// --- DATA WITH ICONS ---
const SYLLABUS = [
  { topic: "Introduction to Flutter & Dart", icon: Terminal },
  { topic: "Widgets, Layouts & Navigation", icon: Layers },
  { topic: "State Management (Provider, Riverpod)", icon: Cpu },
  { topic: "Networking & REST API Integration", icon: Globe },
  { topic: "Firebase Authentication & Firestore", icon: Lock },
  { topic: "Animations & Custom UI Components", icon: Wand2 },
  { topic: "Local Storage (SQFLite, SharedPrefs)", icon: Database },
  { topic: "Deploying to Android & iOS", icon: Smartphone },
];

const LABS = [
  {
    title: "Portfolio App",
    desc: "Build a personal portfolio app with custom widgets.",
    icon: FolderGit2,
  },
  {
    title: "Chat App",
    desc: "Implement Firebase chat with authentication & messages.",
    icon: Users,
  },
  {
    title: "ToDo App",
    desc: "State management practice with local storage.",
    icon: ClipboardList,
  },
  {
    title: "E-Commerce Demo",
    desc: "Product listing, cart, and checkout flow.",
    icon: ShoppingCart,
  },
];

const SKILLS = [
  { text: "Flutter Widgets & UI", icon: Smartphone },
  { text: "Dart Programming", icon: Code2 },
  { text: "State Management", icon: Cpu },
  { text: "Firebase & APIs", icon: Cloud },
  { text: "App Deployment", icon: Rocket },
];

const CAREERS = [
  { role: "Flutter Developer", icon: Smartphone },
  { role: "Mobile App Engineer", icon: Rocket },
  { role: "Cross-Platform Developer", icon: Layers },
  { role: "UI/UX Mobile Specialist", icon: Sparkles },
  { role: "Freelance App Creator", icon: PhoneCall },
];

// Shared hover class for all cards
const cardHoverClass =
  "transition hover:shadow-lg hover:shadow-indigo-300/50 hover:scale-[1.02] rounded-xl bg-white p-4";

export default function Flutter() {
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-100 text-slate-900 overflow-hidden">
      {/* HERO */}
      <header className="max-w-6xl mx-auto px-6 pt-12 pb-16 mt-3">
        <div
          className="rounded-3xl border border-indigo-100 bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-xl overflow-hidden mt-10"
          data-aos="zoom-in"
        >
          <div className="grid md:grid-cols-2 gap-6 p-8 items-center">
            <div className="ms-5">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                Flutter Development - Build Cross-Platform Mobile Apps
              </h1>
              <p className="mt-3 text-sm text-indigo-100 max-w-lg">
                Learn Flutter & Dart to create stunning Android and iOS apps
                with a single codebase. From UI design to Firebase integration,
                get industry-ready.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-indigo-600 font-semibold shadow hover:scale-[1.03] transition"
                >
                  <PlayCircle className="h-5 w-5" /> Enroll Now
                </Link>
              </div>
            </div>
            {/* Hero Illustration */}
            <div
              className="flex justify-center md:justify-end"
              data-aos="fade-left"
            >
              <div className="relative me-5">
                <div className="w-56 h-56 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center shadow-xl">
                  <Smartphone className="h-24 w-24 text-yellow-300" />
                </div>
                <div className="absolute -bottom-4 -left-4 rounded-full bg-emerald-500 text-white text-sm font-bold px-3 py-1 shadow">
                  Cross-Platform
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Syllabus */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-6 text-center" data-aos="fade-up">
          Syllabus
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {SYLLABUS.map((item, i) => (
            <div
              key={i}
              className={`${cardHoverClass} flex items-start gap-3`}
              data-aos="fade-up"
              data-aos-delay={i * 100}
            >
              <item.icon className="h-5 w-5 text-indigo-500 mt-1 animate-float" />
              <span className="font-medium">{item.topic}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Labs */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-6 text-center" data-aos="fade-up">
          Hands-on Labs & Projects
        </h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {LABS.map((lab, i) => (
            <div
              key={i}
              className={`${cardHoverClass} p-5 bg-gradient-to-br from-indigo-50 to-purple-50 border`}
              data-aos="zoom-in"
              data-aos-delay={i * 150}
            >
              <h3 className="font-semibold text-indigo-600 flex items-center gap-2 animate-bounce-slow">
                <lab.icon className="h-4 w-4" /> {lab.title}
              </h3>
              <p className="mt-2 text-sm text-slate-700">{lab.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Skills */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8">
        <div data-aos="fade-right">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-500" /> Skills You'll Gain
          </h3>
          <div className="flex flex-wrap gap-3">
            {SKILLS.map((s, i) => (
              <div
                key={i}
                className={`${cardHoverClass} p-3 border min-w-[180px]`}
                data-aos="fade-up"
                data-aos-delay={i * 100}
              >
                <div className="font-semibold text-slate-800 flex items-center gap-2">
                  <s.icon className="h-4 w-4 text-indigo-400 animate-wiggle" />{" "}
                  {s.text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Careers */}
        <div data-aos="fade-left">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Rocket className="h-5 w-5 text-emerald-500" /> Career Outcomes
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {CAREERS.map((c, i) => (
              <div
                key={i}
                className={`${cardHoverClass} p-4 border`}
                data-aos="fade-up"
                data-aos-delay={i * 100}
              >
                <div className="font-semibold text-slate-800 flex items-center gap-2">
                  <c.icon className="h-4 w-4 text-indigo-400 animate-bounce" />{" "}
                  {c.role}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-6 text-center" data-aos="fade-up">
          Learning Roadmap
        </h2>
        <div
          className="flex items-center gap-4 overflow-x-auto pb-3 no-scrollbar"
          data-aos="fade-up"
        >
          {[
            { title: "Week 1-2", desc: "Dart Basics & Flutter Setup" },
            { title: "Week 3-4", desc: "Widgets & Navigation" },
            { title: "Week 5-6", desc: "State Management & APIs" },
            { title: "Week 7-8", desc: "Firebase, Deployment & Portfolio" },
          ].map((m, i) => (
            <div
              key={i}
              className={`${cardHoverClass} min-w-[220px] border p-5`}
              data-aos="flip-up"
              data-aos-delay={i * 120}
            >
              <div className="font-semibold text-indigo-600">{m.title}</div>
              <div className="text-sm text-slate-600 mt-1">{m.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-6 text-center" data-aos="fade-up">
          Frequently Asked
        </h2>
        <div className="space-y-3">
          {[
            {
              q: "Do I need prior experience?",
              a: "Basic programming helps, but we start from scratch with Dart and Flutter.",
            },
            {
              q: "Will I build real apps?",
              a: "Yes! Labs and projects include chat, e-commerce, and portfolio apps.",
            },
            {
              q: "Can I publish apps?",
              a: "Absolutely — you’ll learn how to deploy apps to both Android & iOS stores.",
            },
          ].map((f, i) => (
            <details
              key={i}
              open={i === 0}
              className="group rounded-lg border px-4 py-3 bg-white shadow-sm transition hover:shadow-lg hover:scale-[1.02]"
              data-aos="fade-right"
              onToggle={() => setOpenFAQ(i)}
            >
              <summary className="flex items-center justify-between cursor-pointer font-medium">
                <span>{f.q}</span>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </summary>
              <div className="mt-2 text-sm text-slate-700">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <footer className="max-w-6xl mx-auto px-6 py-12">
        <div
          className="rounded-3xl p-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-xl"
          data-aos="zoom-in-up"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold">Ready to Build Apps?</h3>
              <p className="mt-1 text-sm">
                Join the Flutter course and launch your own cross-platform apps
                with confidence.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/contact"
                className="px-5 py-3 rounded-xl bg-white text-indigo-600 font-semibold shadow hover:opacity-90"
              >
                Talk to Counsellor
              </Link>
              <Link
                to="/contact"
                className="px-5 py-3 rounded-xl border border-white/30 text-white hover:bg-white/10"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
